import React, { useState, useEffect } from 'react';
import { Mail, Users, Send, Eye, EyeOff, CheckCircle, AlertCircle, Download, Calendar, Globe, FileText, Settings, TestTube, Copy, ExternalLink, Server, Shield, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [activeTab, setActiveTab] = useState('newsletter');

  // Newsletter states
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);

  // SMTP test states
  const [testEmail, setTestEmail] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);

  // Domain info state
  const [domainInfo, setDomainInfo] = useState(null);
  
  // OVH SMTP states
  const [ovhConfig, setOvhConfig] = useState(null);
  const [testOvhEmail, setTestOvhEmail] = useState('');
  const [testingOvh, setTestingOvh] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        setMessage('Authentification réussie');
        setMessageType('success');
        loadSubscriptions();
        loadDomainInfo();
        loadOvhConfig();
      } else {
        setMessage('Email ou mot de passe incorrect');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Erreur de connexion au serveur');
      setMessageType('error');
    }

    setLoading(false);
  };

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter-subscriptions`);
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data);
      }
    } catch (error) {
      console.error('Erreur chargement abonnés:', error);
    }
  };

  const loadDomainInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/domain-info`);
      if (response.ok) {
        const data = await response.json();
        setDomainInfo(data);
      }
    } catch (error) {
      console.error('Erreur chargement infos domaine:', error);
    }
  };

  const loadOvhConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/ovh-smtp-config`);
      if (response.ok) {
        const data = await response.json();
        setOvhConfig(data);
      }
    } catch (error) {
      console.error('Erreur chargement config OVH:', error);
    }
  };

  const testOvhSmtp = async (e) => {
    e.preventDefault();
    setTestingOvh(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-ovh-smtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ test_email: testOvhEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Test SMTP OVH réussi ! Email envoyé à ${testOvhEmail}`);
        setMessageType('success');
      } else {
        setMessage(data.detail || 'Erreur lors du test SMTP OVH');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Erreur de connexion pour le test SMTP OVH');
      setMessageType('error');
    }

    setTestingOvh(false);
  };

  const sendNewsletter = async (e) => {
    e.preventDefault();
    setSendingNewsletter(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: newsletterSubject,
          content: newsletterContent
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Newsletter envoyée avec succès à ${data.sent_count} abonnés`);
        setMessageType('success');
        setNewsletterSubject('');
        setNewsletterContent('');
      } else {
        setMessage('Erreur lors de l\'envoi de la newsletter');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Erreur de connexion lors de l\'envoi');
      setMessageType('error');
    }

    setSendingNewsletter(false);
  };

  const testSmtpConnection = async (e) => {
    e.preventDefault();
    setTestingSmtp(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-smtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ test_email: testEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Test SMTP réussi ! Email envoyé à ${testEmail}`);
        setMessageType('success');
      } else {
        setMessage(data.detail || 'Erreur lors du test SMTP');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Erreur de connexion pour le test SMTP');
      setMessageType('error');
    }

    setTestingSmtp(false);
  };

  const exportSubscriptions = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Email,Date d'inscription,Statut\n"
      + subscriptions.map(sub => `${sub.email},${new Date(sub.timestamp).toLocaleDateString('fr-FR')},${sub.status}`).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `newsletter_subscriptions_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Copié dans le presse-papiers');
    setMessageType('success');
    setTimeout(() => setMessage(''), 3000);
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <Card className="shadow-2xl border-0">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-lg">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
                  <Settings className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2">TICLIC.NET</h2>
                <p className="text-indigo-100">Espace d'administration</p>
              </div>
            </div>
            
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="moznet@ticlic.net"
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Settings className="h-5 w-5 mr-2" />
                  )}
                  {loading ? 'Connexion...' : 'Accéder à l\'administration'}
                </button>
              </form>
              
              {message && (
                <div className={`mt-4 p-3 rounded-lg flex items-center text-sm ${
                  messageType === 'success' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {messageType === 'success' ? (
                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  )}
                  {message}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Domain Configuration Info */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Configuration Domaine
              </h3>
              <p className="text-blue-700 text-sm mb-2">
                <strong>Domaine cible:</strong> ticlic.net
              </p>
              <p className="text-blue-600 text-xs">
                Une fois connecté, accédez à l'onglet "Configuration Domaine" pour les instructions de configuration DNS.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="h-8 w-8 mr-3" />
              <h1 className="text-2xl font-bold">TICLIC.NET Administration</h1>
            </div>
            <Button 
              onClick={() => setIsAuthenticated(false)}
              variant="outline"
              className="text-white border-white hover:bg-white hover:text-indigo-600"
            >
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('newsletter')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'newsletter'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Mail className="h-4 w-4 mr-2 inline" />
              Newsletter
            </button>
            <button
              onClick={() => setActiveTab('smtp')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'smtp'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Server className="h-4 w-4 mr-2 inline" />
              SMTP Brevo
            </button>
            <button
              onClick={() => setActiveTab('ovh')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'ovh'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Mail className="h-4 w-4 mr-2 inline" />
              SMTP OVH
            </button>
            <button
              onClick={() => setActiveTab('domain')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'domain'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Globe className="h-4 w-4 mr-2 inline" />
              Configuration Domaine
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <div className="space-y-8">
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Abonnés</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{subscriptions.length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Abonnés Actifs</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{subscriptions.filter(s => s.status === 'active').length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Aujourd'hui</CardTitle>
                  <Calendar className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {subscriptions.filter(s => 
                      new Date(s.timestamp).toDateString() === new Date().toDateString()
                    ).length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Send Newsletter Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Send className="h-5 w-5 mr-2" />
                  Envoyer une Newsletter
                </CardTitle>
                <CardDescription>
                  Composez et envoyez une newsletter à tous les abonnés actifs via Brevo SMTP
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={sendNewsletter} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sujet
                    </label>
                    <input
                      type="text"
                      value={newsletterSubject}
                      onChange={(e) => setNewsletterSubject(e.target.value)}
                      placeholder="Nouveautés TICLIC.NET - Janvier 2025"
                      required
                      disabled={sendingNewsletter}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contenu HTML
                    </label>
                    <textarea
                      value={newsletterContent}
                      onChange={(e) => setNewsletterContent(e.target.value)}
                      placeholder="<html><body><h1>Newsletter TICLIC.NET</h1><p>Contenu de votre newsletter...</p></body></html>"
                      required
                      disabled={sendingNewsletter}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 font-mono text-sm"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={sendingNewsletter || subscriptions.filter(s => s.status === 'active').length === 0}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {sendingNewsletter ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {sendingNewsletter ? 'Envoi en cours...' : `Envoyer à ${subscriptions.filter(s => s.status === 'active').length} abonnés`}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Subscriptions List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="h-5 w-5 mr-2" />
                      Abonnés Newsletter
                    </CardTitle>
                    <CardDescription>
                      Liste de tous les abonnés à la newsletter TICLIC.NET
                    </CardDescription>
                  </div>
                  <Button onClick={exportSubscriptions} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Aucun abonné pour le moment</p>
                    <p className="text-sm text-gray-400">Les nouvelles inscriptions apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {subscriptions.map((subscription, index) => (
                      <div key={subscription.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <Mail className="h-5 w-5 text-indigo-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{subscription.email}</p>
                            <p className="text-xs text-gray-500">
                              Inscrit le {new Date(subscription.timestamp).toLocaleDateString('fr-FR')} à {new Date(subscription.timestamp).toLocaleTimeString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                            {subscription.status === 'active' ? 'Actif' : 'Inactif'}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => copyToClipboard(subscription.email)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* SMTP Configuration Tab */}
        {activeTab === 'smtp' && (
          <div className="space-y-8">
            {/* SMTP Configuration Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Server className="h-5 w-5 mr-2" />
                  Configuration SMTP Brevo
                </CardTitle>
                <CardDescription>
                  Configuration et test de l'envoi d'emails via Brevo SMTP
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SMTP Configuration Details */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Zap className="h-4 w-4 mr-2" />
                    Paramètres SMTP Configurés
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Serveur SMTP :</p>
                      <p className="font-mono bg-white p-2 rounded border">smtp-relay.brevo.com</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Port :</p>
                      <p className="font-mono bg-white p-2 rounded border">587 (TLS)</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Expéditeur :</p>
                      <p className="font-mono bg-white p-2 rounded border">contact@ticlic.net</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Nom expéditeur :</p>
                      <p className="font-mono bg-white p-2 rounded border">TICLIC.NET</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-gray-600">Clé API SMTP :</p>
                      <p className="font-mono bg-white p-2 rounded border text-xs">
                        xsmtpsib-978c...UT0WvsX3z5EGmtOR (configurée)
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard('xsmtpsib-978c70a7b6812173afa4fe0b02bdabc37d14f671337e452e2436d29cca0135dc-UT0WvsX3z5EGmtOR')} className="ml-2">
                          <Copy className="h-3 w-3" />
                        </Button>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Test SMTP */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <TestTube className="h-4 w-4 mr-2" />
                    Test de Connexion SMTP
                  </h3>
                  <form onSubmit={testSmtpConnection} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de test
                      </label>
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="moznet@ticlic.net"
                        required
                        disabled={testingSmtp}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={testingSmtp}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      {testingSmtp ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <TestTube className="h-4 w-4 mr-2" />
                      )}
                      {testingSmtp ? 'Test en cours...' : 'Tester la connexion SMTP'}
                    </Button>
                  </form>
                </div>

                {/* SMTP Status */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Important - Configuration Post-Déploiement
                  </h3>
                  <p className="text-yellow-700 text-sm mb-3">
                    Une fois le domaine ticlic.net configuré et la plateforme déployée :
                  </p>
                  <ol className="list-decimal list-inside text-yellow-700 text-sm space-y-1">
                    <li>Vérifiez que l'expéditeur contact@ticlic.net est vérifié dans Brevo</li>
                    <li>Configurez les enregistrements SPF/DKIM pour le domaine ticlic.net</li>
                    <li>Testez l'envoi SMTP avec un email réel</li>
                    <li>Validez la réception et la réputation de l'expéditeur</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* OVH SMTP Tab */}
        {activeTab === 'ovh' && (
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  Configuration SMTP OVH
                </CardTitle>
                <CardDescription>
                  Configuration et test de l'envoi d'emails via SMTP OVH pour ticlic.net
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* OVH Configuration Details */}
                {ovhConfig && (
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
                      <Settings className="h-4 w-4 mr-2" />
                      Paramètres SMTP OVH Configurés
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Serveur SMTP :</p>
                        <p className="font-mono bg-white p-2 rounded border">{ovhConfig.smtp_host}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Port :</p>
                        <p className="font-mono bg-white p-2 rounded border">{ovhConfig.smtp_port}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Email expéditeur :</p>
                        <p className="font-mono bg-white p-2 rounded border">{ovhConfig.smtp_email}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Statut :</p>
                        <p className={`font-mono p-2 rounded border ${
                          ovhConfig.smtp_configured 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {ovhConfig.smtp_configured ? 'Configuré' : 'Non configuré'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Test OVH SMTP */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <TestTube className="h-4 w-4 mr-2" />
                    Test de Connexion SMTP OVH
                  </h3>
                  <form onSubmit={testOvhSmtp} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de test
                      </label>
                      <input
                        type="email"
                        value={testOvhEmail}
                        onChange={(e) => setTestOvhEmail(e.target.value)}
                        placeholder="moznet@ticlic.net"
                        required
                        disabled={testingOvh}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={testingOvh}
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                    >
                      {testingOvh ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <TestTube className="h-4 w-4 mr-2" />
                      )}
                      {testingOvh ? 'Test en cours...' : 'Tester SMTP OVH'}
                    </Button>
                  </form>
                </div>

                {/* OVH SMTP Configuration Guide */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    Configuration Automatique OVH
                  </h3>
                  <div className="text-blue-700 text-sm space-y-2">
                    <p>✅ <strong>Email configuré</strong> : contact@ticlic.net</p>
                    <p>✅ <strong>Serveur SMTP</strong> : ssl0.ovh.net (port 587)</p>
                    <p>✅ <strong>Authentification</strong> : Configurée automatiquement</p>
                    <p>✅ <strong>Chiffrement</strong> : STARTTLS activé</p>
                    <p>⚠️ <strong>Domaine requis</strong> : Fonctionnel après déploiement sur ticlic.net</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Domain Configuration Tab */}
        {activeTab === 'domain' && (
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Globe className="h-5 w-5 mr-2" />
                  Configuration du Domaine ticlic.net
                </CardTitle>
                <CardDescription>
                  Instructions pour rattacher le domaine ticlic.net à cette plateforme Emergent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SSL Let's Encrypt Info */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Certificat SSL Let's Encrypt - Automatique
                  </h3>
                  <div className="text-green-700 text-sm space-y-2">
                    <p>✅ <strong>Configuration automatique</strong> : Emergent génère automatiquement le certificat SSL Let's Encrypt</p>
                    <p>✅ <strong>Renouvellement automatique</strong> : Certificat renouvelé automatiquement avant expiration</p>
                    <p>✅ <strong>HTTPS obligatoire</strong> : Redirection automatique HTTP → HTTPS</p>
                    <p>✅ <strong>Aucune action requise</strong> : SSL configuré automatiquement après vérification du domaine</p>
                  </div>
                </div>

                {/* Current Configuration */}
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-indigo-800 mb-3">Configuration Actuelle</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Domaine cible :</p>
                      <p className="font-mono bg-white p-2 rounded border">ticlic.net</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Plateforme :</p>
                      <p className="font-mono bg-white p-2 rounded border">Emergent Kubernetes</p>
                    </div>
                    <div>
                      <p className="text-gray-600">URL de développement :</p>
                      <p className="font-mono bg-white p-2 rounded border text-xs">knowledge-central-1.preview.emergentagent.com</p>
                    </div>
                    <div>
                      <p className="text-gray-600">SSL :</p>
                      <p className="font-mono bg-white p-2 rounded border text-green-600">Let's Encrypt (Auto)</p>
                    </div>
                  </div>
                </div>

                {/* Deployment Steps */}
                {domainInfo && domainInfo.deployment_steps && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Étapes de Déploiement Emergent</h3>
                    
                    {domainInfo.deployment_steps.map((step, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-800 rounded-full flex items-center justify-center font-bold text-sm">
                            {step.step}
                          </div>
                          <div className="flex-grow">
                            <h4 className="font-medium text-gray-900 mb-1">{step.title}</h4>
                            <p className="text-gray-600 text-sm mb-2">{step.description}</p>
                            <Badge variant="outline" className="text-xs">
                              ⏱️ {step.estimated_time}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* DNS Configuration */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Configuration DNS (Après Déploiement)</h3>
                  
                  {domainInfo && domainInfo.dns_instructions && (
                    <div className="space-y-3">
                      {/* A Record */}
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">Enregistrement A (Principal)</h4>
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(`@ A ${domainInfo.dns_instructions.A_record.value}`)}>
                            <Copy className="h-3 w-3 mr-1" />
                            Copier
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500">Type :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">A</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Nom :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">@</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Valeur :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded text-xs">{domainInfo.dns_instructions.A_record.value}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">TTL :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">{domainInfo.dns_instructions.A_record.ttl}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{domainInfo.dns_instructions.A_record.description}</p>
                      </div>

                      {/* CNAME Record */}
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">Enregistrement CNAME (Optionnel)</h4>
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(`www CNAME ticlic.net`)}>
                            <Copy className="h-3 w-3 mr-1" />
                            Copier
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500">Type :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">CNAME</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Nom :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">www</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Valeur :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">ticlic.net</p>
                          </div>
                          <div>
                            <p className="text-gray-500">TTL :</p>
                            <p className="font-mono bg-gray-100 p-1 rounded">{domainInfo.dns_instructions.CNAME_www.ttl}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{domainInfo.dns_instructions.CNAME_www.description}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Important Notes */}
                {domainInfo && domainInfo.important_notes && (
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-yellow-800 mb-3 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Points Importants
                    </h3>
                    <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1">
                      {domainInfo.important_notes.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* External Tools */}
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" className="flex items-center">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <a href="https://www.whatsmydns.net/#A/ticlic.net" target="_blank" rel="noopener noreferrer">
                      Vérifier propagation DNS
                    </a>
                  </Button>
                  <Button variant="outline" className="flex items-center">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <a href="https://www.ssllabs.com/ssltest/analyze.html?d=ticlic.net" target="_blank" rel="noopener noreferrer">
                      Tester SSL (après config)
                    </a>
                  </Button>
                  <Button variant="outline" className="flex items-center">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <a href="https://dnschecker.org/#A/ticlic.net" target="_blank" rel="noopener noreferrer">
                      DNS Checker Global
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SMTP Tab */}
        {activeTab === 'smtp' && (
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TestTube className="h-5 w-5 mr-2" />
                  Test de Connexion SMTP
                </CardTitle>
                <CardDescription>
                  Testez la connexion SMTP Brevo depuis le domaine ticlic.net
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={testSmtpConnection} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email de test
                    </label>
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="moznet@ticlic.net"
                      required
                      disabled={testingSmtp}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={testingSmtp}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    {testingSmtp ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <TestTube className="h-4 w-4 mr-2" />
                    )}
                    {testingSmtp ? 'Test en cours...' : 'Envoyer email de test'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Global Message */}
        {message && (
          <div className={`mt-6 p-4 rounded-lg flex items-center text-sm ${
            messageType === 'success' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {messageType === 'success' ? (
              <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            )}
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;